import React from 'react';
import { Box, Flex, Text, Switch } from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { FiX, FiSliders, FiBell, FiVolume2, FiClock } from 'react-icons/fi';
import { getModeAccent } from '../themes';
import { MODES } from '../hooks/useTimer';

const SectionLabel = ({ icon: Icon, label, theme }) => (
  <Flex align="center" gap={2} mb={3}>
    <Icon size={13} style={{ color: theme.textMuted }} />
    <Text fontSize="11px" fontWeight="500" letterSpacing="1.5px" textTransform="uppercase"
      style={{ color: theme.textMuted }}
    >
      {label}
    </Text>
  </Flex>
);

const DurationRow = ({ label, value, min, max, onChange, accent, theme }) => (
  <Flex align="center" justify="space-between" gap={3}>
    <Text fontSize="13px" style={{ color: theme.textPrimary, minWidth: '100px' }}>
      {label}
    </Text>
    <Flex align="center" gap={2} flex={1}>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="fl-slider"
        style={{ flex: 1, '--fl-accent': accent }}
      />
      <Text
        className="fl-mono"
        fontSize="13px"
        fontWeight="400"
        style={{ color: accent, minWidth: '28px', textAlign: 'right' }}
      >
        {value}m
      </Text>
    </Flex>
  </Flex>
);

const ToggleRow = ({ label, desc, checked, onChange, accent, theme }) => (
  <Flex align="flex-start" justify="space-between" gap={3}>
    <Box flex={1}>
      <Text fontSize="13px" style={{ color: theme.textPrimary }}>{label}</Text>
      {desc && <Text fontSize="11px" mt={0.5} style={{ color: theme.textDim }}>{desc}</Text>}
    </Box>
    <Switch
      isChecked={checked}
      onChange={(e) => onChange(e.target.checked)}
      size="sm"
      sx={{
        '& .chakra-switch__track': {
          bg: checked ? accent : 'rgba(255,255,255,0.12)',
        },
      }}
    />
  </Flex>
);

const SettingsPanel = ({ isOpen, onClose, settings, onSettingsChange, theme }) => {
  const accent = getModeAccent(theme, MODES.FOCUS);

  const update = (key) => (val) => onSettingsChange({ [key]: val });

  const requestNotifPermission = async (enabled) => {
    if (enabled && 'Notification' in window && Notification.permission === 'default') {
      const perm = await Notification.requestPermission();
      onSettingsChange({ notificationsEnabled: perm === 'granted' });
    } else {
      onSettingsChange({ notificationsEnabled: enabled });
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 300,
              backdropFilter: 'blur(4px)',
            }}
          />

          {/* Drawer */}
          <motion.div
            key="drawer"
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0 }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              width: '340px',
              maxWidth: '100vw',
              zIndex: 400,
              display: 'flex',
              flexDirection: 'column',
              background: 'rgba(8,6,20,0.97)',
              borderLeft: '1px solid rgba(255,255,255,0.1)',
              backdropFilter: 'blur(24px)',
              overflowY: 'auto',
            }}
            className="fl-scroll"
          >
            {/* Drawer header */}
            <Flex align="center" justify="space-between" p={5} pb={4}
              style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}
            >
              <Flex align="center" gap={2}>
                <FiSliders size={16} style={{ color: theme.textMuted }} />
                <Text fontSize="16px" fontWeight="600" style={{ color: theme.textPrimary }}>
                  Settings
                </Text>
              </Flex>
              <Box
                as="button"
                onClick={onClose}
                p={1.5}
                borderRadius="lg"
                cursor="pointer"
                style={{
                  color: theme.textMuted,
                  background: 'transparent',
                  border: 'none',
                  transition: 'all 0.15s ease',
                }}
                _hover={{ color: theme.textPrimary, background: 'rgba(255,255,255,0.08)' }}
              >
                <FiX size={18} />
              </Box>
            </Flex>

            <Box p={5} flex={1}>
              {/* Durations */}
              <Box mb={6}>
                <SectionLabel icon={FiClock} label="Durations" theme={theme} />
                <Flex direction="column" gap={4}>
                  <DurationRow
                    label="Focus"
                    value={settings.focusDuration}
                    min={5} max={90}
                    onChange={update('focusDuration')}
                    accent={theme.focusAccent}
                    theme={theme}
                  />
                  <DurationRow
                    label="Short break"
                    value={settings.shortBreakDuration}
                    min={1} max={30}
                    onChange={update('shortBreakDuration')}
                    accent={theme.breakAccent}
                    theme={theme}
                  />
                  <DurationRow
                    label="Long break"
                    value={settings.longBreakDuration}
                    min={5} max={60}
                    onChange={update('longBreakDuration')}
                    accent={theme.longBreakAccent}
                    theme={theme}
                  />
                </Flex>
              </Box>

              {/* Day flow — all-day rhythm */}
              <Box mb={6} pt={5} style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                <SectionLabel icon={FiSliders} label="Day flow" theme={theme} />
                <Flex direction="column" gap={4}>
                  <DurationRow
                    label="Focus blocks until long break"
                    value={settings.sessionsUntilLongBreak}
                    min={2} max={8}
                    onChange={update('sessionsUntilLongBreak')}
                    accent={accent}
                    theme={theme}
                  />
                  <ToggleRow
                    label="Auto-start breaks"
                    desc="Begin the next break as soon as focus ends"
                    checked={settings.autoStartBreaks}
                    onChange={update('autoStartBreaks')}
                    accent={accent}
                    theme={theme}
                  />
                  <ToggleRow
                    label="Auto-start focus"
                    desc="Return to focus when a break ends"
                    checked={settings.autoStartFocus}
                    onChange={update('autoStartFocus')}
                    accent={accent}
                    theme={theme}
                  />
                </Flex>
              </Box>

              {/* Sound */}
              <Box mb={6} pt={5} style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                <SectionLabel icon={FiVolume2} label="Sound" theme={theme} />
                <Flex direction="column" gap={4}>
                  <ToggleRow
                    label="Session bell"
                    desc="Soft chime when a session completes"
                    checked={settings.soundEnabled}
                    onChange={update('soundEnabled')}
                    accent={accent}
                    theme={theme}
                  />
                  <ToggleRow
                    label="Ticking sound"
                    desc="Subtle tick each second while running"
                    checked={settings.tickingEnabled}
                    onChange={update('tickingEnabled')}
                    accent={accent}
                    theme={theme}
                  />
                </Flex>
              </Box>

              {/* Notifications */}
              <Box pt={5} style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                <SectionLabel icon={FiBell} label="Notifications" theme={theme} />
                <ToggleRow
                  label="Desktop notifications"
                  desc="Alert when a session ends (requires permission)"
                  checked={settings.notificationsEnabled}
                  onChange={requestNotifPermission}
                  accent={accent}
                  theme={theme}
                />
              </Box>
            </Box>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SettingsPanel;
